import {
    buildVisitorAppAdoptionAggregations,
    parseVisitorAppAdoptionResults,
} from '@pendo/middleware';
import { i18n_t as $t } from '@/utils/i18n';
import moment from '@/utils/moment-wrapper';
import { addCommasToNumber } from '@/utils/formatters';
import { SIGNAL_TYPES } from './signals.constants.js';

export function buildAggregations (signals, context) {
    const aggregations = [];
    const breakpoints = [];

    signals.forEach((signal) => {
        switch (signal) {
            case SIGNAL_TYPES.VISITOR_ADOPTION: {
                const aggs = buildVisitorAdoptionAggregations(context);
                aggregations.push(...aggs);
                breakpoints.push(aggregations.length);
                break;
            }
            default:
                throw new Error(`Unknown signal type: ${signal}`);
        }
    });

    return [aggregations, breakpoints];
}

export function parseAggregationResults (signals, breakpoints, rawData) {
    const extractedData = {};
    let previousBreakpoint = 0;

    breakpoints.forEach((breakpoint, index) => {
        const relevantData = rawData.slice(previousBreakpoint, breakpoint);
        previousBreakpoint = breakpoint;

        switch (signals[index]) {
            case SIGNAL_TYPES.VISITOR_ADOPTION:
                extractedData[SIGNAL_TYPES.VISITOR_ADOPTION] = parseVisitorAppAdoptionResults(relevantData);
                break;
            default:
                throw new Error(`Unknown signal type: ${signals[index]}`);
        }
    });

    return extractedData;
}

export function createSignals (signalsToFetch, extractedData) {
    const signals = [];

    signalsToFetch.forEach((signal) => {
        const dataPoints = extractedData[signal];

        switch (signal) {
            case SIGNAL_TYPES.VISITOR_ADOPTION:
                signals.push(createVisitorAdoptionSignal(dataPoints));
                break;
            default:
                throw new Error(`Unknown signal type: ${signal}`);
        }
    });

    return signals;
}

// helper functions for formatting date ranges

function formatWeekLabel (start, end) {
    return start.isSame(end, 'month')
        ? `${start.format('MMMM D')}-${end.format('D')}`
        : `${start.format('MMMM D')}-${end.format('MMMM D')}`;
}

function get7DayPeriodTimestamps () {
    function getTimestampForDayOffset (offset) { return moment(undefined, { ignoreTimeZone: true }).subtract(offset, 'days'); }

    return [getTimestampForDayOffset(1), getTimestampForDayOffset(7), getTimestampForDayOffset(8), getTimestampForDayOffset(14)];
}

// functions specific to visitor adoption

function buildVisitorAdoptionAggregations ({ appId, hasShowAnonymousFlag }) {
    const { iterativeAggregations } = buildVisitorAppAdoptionAggregations({
        appId,
        hasShowAnonymousFlag,
    });

    return iterativeAggregations.map(({ agg: { name, pipeline } }) => ({
        name,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createVisitorAdoptionSignal (dataPoints) {

    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);

    const { appId, tagName, delta, previousDataPoint, currentDataPoint } = dataPoints;

    return {
        type: SIGNAL_TYPES.VISITOR_ADOPTION,
        kind: 'visitor',
        title: $t('signals.Visitor_adoption'),

        appId,
        tagName,
        showAppTag: true,

        delta,
        previousDataPoint,
        currentDataPoint,

        currentPeriodLabel,
        previousPeriodLabel,

        textBeforeTagName: $t('signals.visitorAdoptionTextBefore'),
        textAfterTagName: buildVisitorAdoptionTextAfter({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }),
    };
}

function buildVisitorAdoptionTextAfter ({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }) {
    const currentCount = addCommasToNumber(currentDataPoint);

    if (previousDataPoint === 0 && currentDataPoint > 0) {
        return $t('signals.visitorAdoptionTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentCount,
        });
    }

    const previousCount = addCommasToNumber(previousDataPoint);

    if (delta === null || delta === 0) {
        return $t('signals.visitorAdoptionTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentCount,
            previousCount,
        });
    }

    const key = delta > 0 ? 'signals.visitorAdoptionTextAfterIncrease' : 'signals.visitorAdoptionTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentCount,
        previousCount,
        delta: addCommasToNumber(Math.abs(delta)),
    });
}
